/**
 * The troop carrier (ROUND4 §1, `EnemyKind` `truck`). The one vehicle with no prop model to reuse:
 * a six-wheeled lorry with a canvas tilt over the bed and the men riding inside it. Same family as
 * the staff car and the launcher - nose to the SOUTH at yaw 0, so `Enemy.facing` 0 drives it at the
 * camera and the grille, headlamps, plate and bumper are the faces it shows.
 *
 * It is an enemy, not a prop, so it is held to the VEHICLE triangle class (900) rather than a
 * prop budget, and `world/machineModels` maps the kind to it.
 */

import type { Palette } from '../palette';
import { MIN_BAR_METRES } from './budgets';
import type { KitBuilder } from './flora';
import { type BoxColors, MeshBuilder, place } from './MeshBuilder';
import { sided, snowPatch, wheel } from './parts';
import { burnt, debris, scorch } from './wreckParts';

/** Axle positions along z: one at the front, a bogie of two under the bed. */
const AXLES = [2.0, -1.4, -2.55];
const TRACK = 1.0;
/** The bed and its tilt run from here to the tailgate; the cab and bonnet are forward of it. */
const TILT = { y: 1.1, h: 1.15, z: -1.2, d: 3.9, w: 2.24 };
/** Centre of a headlamp: the body draws it, `truckLamps` puts the glow just proud of it. */
const LAMP = { x: 0.62, y: 1.05, z: 2.93 };

/** Bare hoops over the bed, the shape that still says "lorry" when the canvas has burnt off it. */
function hoops(mb: MeshBuilder, colors: BoxColors, top: number): void {
  const bar = MIN_BAR_METRES;
  for (let i = 0; i < 4; i++) {
    const z = TILT.z - TILT.d / 2 + 0.45 + (i * (TILT.d - 0.9)) / 3;
    for (const sx of [-1, 1]) mb.box(bar, top - 0.95, bar, colors, place(sx * (TILT.w / 2 - 0.08), 0.95, z));
    mb.box(TILT.w - 0.1, bar, bar, colors, place(0, top, z));
  }
}

/**
 * Troop carrier: long bonnet and a boxy cab forward, a canvas tilt over the bed behind, six wheels.
 * Cab and chassis in the enemy's uniform ramp (as the staff car, which stands off every biome's
 * ground); the tilt in army drab, as the launcher's bodywork is.
 *
 * `laden` is the tell mission 34 turns on - which lorry to mine. Loaded, the canvas runs the whole
 * bed and is LACED SHUT across the back. Empty, it is rolled forward over the cab end and the rear
 * half is bare hoops over an open bed with the tailgate down: two silhouettes, not two colours, so
 * it reads from any heading at 18 px/m. `world/Machines` swaps them off `Enemy.vehicle.aboard`.
 */
function lorry(p: Palette, laden: boolean): MeshBuilder {
  const mb = new MeshBuilder();
  const lo = p.color('uniformEnemy.0');
  const paint: BoxColors = { all: p.color('uniformEnemy.1'), top: p.color('uniformEnemy.2'), left: lo, right: lo };
  for (const sx of [-1, 1]) for (const z of AXLES) wheel(mb, p, [sx * TRACK, 0.45, z], 0.52, 0.36, 6);
  mb.box(1.9, 0.35, 6.0, lo, place(0, 0.5, 0));

  // Bonnet, cab and the glass: a band of dark under a roof, as the staff car has.
  mb.box(1.7, 0.8, 1.4, paint, place(0, 0.85, 2.25));
  mb.box(2.1, 1.5, 1.6, paint, place(0, 0.85, 0.9));
  mb.box(2.16, 0.12, 1.66, { all: lo, top: p.color('uniformEnemy.2') }, place(0, 2.35, 0.9));
  mb.box(1.6, 0.6, 0.06, p.color('ink.1'), place(0, 1.55, 1.72));
  for (const sx of [-1, 1]) {
    mb.box(0.06, 0.5, 0.9, p.color('ink.1'), place(sx * 1.06, 1.55, 0.85));
    // The door, outlined in the dark step with the unit's mark on it: what a lorry shows side-on.
    mb.box(0.05, 1.3, 0.05, lo, place(sx * 1.06, 0.9, 0.12));
    mb.box(0.3, 0.12, 0.04, p.color('enemyAccent.0'), place(sx * 1.07, 1.15, 0.6));
  }
  snowPatch(mb, p, 0, 2.47, 0.9, 1.5, 1.1);

  // South face: grille, headlamps, bumper and plate - the markings the fixed camera always sees.
  mb.box(1.2, 0.55, 0.06, p.color('ink.0'), place(0, 0.95, 2.96));
  for (const sx of [-1, 1]) mb.box(0.28, 0.24, 0.1, p.color('fire.3'), place(sx * LAMP.x, LAMP.y, LAMP.z));
  mb.box(2.0, 0.16, 0.14, p.color('metal.lo'), place(0, 0.62, 2.98));
  mb.box(0.5, 0.16, 0.04, p.color('paper.0'), place(0, 0.76, 3.02));

  // Bed and hoops, then the canvas: over all of it when loaded, rolled forward over the cab end when not.
  mb.box(2.3, 0.25, 4.0, lo, place(0, 0.85, TILT.z));
  const ribs = { all: p.color('canvas.lo'), top: p.color('canvas.mid') };
  hoops(mb, ribs, TILT.y + TILT.h - 0.02);
  const tail = TILT.z - TILT.d / 2;
  const covered = laden ? TILT.d : TILT.d * 0.52;
  const cz = TILT.z + TILT.d / 2 - covered / 2;
  mb.box(TILT.w, TILT.h, covered, sided(p, 'canvas', p.color('canvas.hi')), place(0, TILT.y, cz));
  mb.with(place(0, TILT.y + TILT.h, cz, Math.PI / 2), () => {
    mb.gable(covered, 0.35, TILT.w, p.color('canvas.hi'), undefined, p.color('canvas.mid'));
  });
  snowPatch(mb, p, 0, TILT.y + TILT.h + 0.18, cz, 1.1, covered * 0.6);
  if (laden) {
    // Laced shut across the back, and the tailgate up: nobody can see in, which is the point.
    mb.box(TILT.w - 0.06, TILT.h, 0.06, p.color('canvas.mid'), place(0, TILT.y, tail + 0.03));
    for (const roll of [0.62, -0.62]) {
      mb.box(2.1, 0.09, 0.04, p.color('canvas.lo'), place(0, TILT.y + TILT.h / 2, tail - 0.02, 0, 1, 0, roll), true);
    }
    mb.box(TILT.w, 0.7, 0.08, paint, place(0, 0.85, -3.16));
  } else {
    // Open and empty: the dark inside of the bed under bare hoops, and the tailgate dropped flat.
    mb.box(TILT.w - 0.2, 0.55, TILT.d - covered - 0.1, p.color('ink.0'), place(0, 1.1, tail + (TILT.d - covered) / 2));
    mb.box(TILT.w, 0.7, 0.08, paint, place(0, 0.9, -3.16, 0, 1, -Math.PI / 2));
  }
  mb.box(1.4, 0.13, 0.05, p.color('enemyAccent.0'), place(0, 1.3, -3.2));
  return mb;
}

/** The lorry as it rides with men in it: canvas over the whole bed, laced shut at the back. */
export const truck: KitBuilder = (p) => lorry(p, true);

/** The same lorry with nobody aboard: canvas rolled forward, bare hoops, tailgate down. */
export const truckEmpty: KitBuilder = (p) => lorry(p, false);

/** The headlamps alone, a shade proud of the body's: drawn self-lit after dark by `world/Machines`. */
export const truckLamps: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  for (const sx of [-1, 1]) mb.box(0.3, 0.26, 0.05, p.color('fire.3'), place(sx * LAMP.x, LAMP.y - 0.01, LAMP.z + 0.06));
  return mb;
};

/**
 * Burnt-out lorry, slewed where it stopped: the cab gutted and leaning, the canvas gone off the bed
 * and the bare hoops standing over it - the silhouette that says "lorry" with everything else black.
 */
export const truckWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  scorch(mb, p, 3.3, 7.0);
  mb.with(place(0, 0, 0, rng.range(0.1, 0.25), 1, 0, -0.13), () => {
    for (const sx of [-1, 1]) for (const z of AXLES) wheel(mb, p, [sx * TRACK, 0.4, z], 0.5, 0.34, 6, 'ink.0');
    mb.box(1.9, 0.35, 6.0, p.color('ink.0'), place(0, 0.42, 0));
    // Cab: four black walls with no glass, its roof dropped into it, the bonnet burst open.
    const gutted = { all: p.color('ink.1'), top: p.color('ink.0'), front: p.color('ink.0') };
    mb.box(2.1, 1.35, 1.6, gutted, place(0, 0.77, 0.9));
    mb.box(2.2, 0.12, 1.7, burnt(p), place(0.15, 2.05, 0.95, 0.2, 1, 0.22, -0.18));
    mb.box(1.7, 0.6, 1.4, gutted, place(0, 0.77, 2.25));
    mb.box(1.1, 0.5, 0.9, p.color('metal.lo'), place(-0.1, 1.3, 2.3, 0.15, 1, 0.3));
    mb.box(2.0, 0.16, 0.14, p.color('ink.0'), place(0, 0.54, 2.98));
    // The bed with its canvas burnt off, and two shreds of it still on the hoops.
    mb.box(2.3, 0.2, 4.0, p.color('ink.0'), place(0, 0.77, TILT.z));
    hoops(mb, burnt(p), TILT.y + TILT.h - 0.12);
    for (const z of [TILT.z - 1.1, TILT.z + 0.9]) {
      mb.box(0.9, 0.5, 0.06, p.color('canvas.lo'), place(rng.range(-0.9, 0.9), 1.4, z, 0, 1, 0, rng.range(-0.4, 0.4)));
    }
    mb.box(TILT.w, 0.6, 0.08, burnt(p), place(0, 0.77, -3.16));
  });
  // Thrown clear: enough of it to cover the hulk the simulation stops you at (VEHICLES.wreckRadius).
  debris(mb, p, rng, 8, 1.85, 3.2);
  return mb;
};
